import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash, ToggleLeft, ToggleRight, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export const SubLocationManager = ({ site, open, onOpenChange }) => {
    const [subs, setSubs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tick, setTick] = useState(0);
    const [newName, setNewName] = useState("");
    const [newCode, setNewCode] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editCode, setEditCode] = useState("");

    const reload = useCallback(() => setTick((t) => t + 1), []);

    useEffect(() => {
        if (!open || !site) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/sites/${site.id}/sublocations`)
            .then(({ data }) => { if (!cancelled) setSubs(Array.isArray(data) ? data : []); })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, site, tick]);

    const add = async () => {
        if (!newName.trim()) { toast.error("Name is required"); return; }
        try {
            await api.post("/sublocations", {
                site_id: site.id, name: newName.trim(), job_code: newCode.trim(), active: true,
            });
            setNewName(""); setNewCode("");
            toast.success("Sub-location added");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const saveEdit = async (sub) => {
        try {
            await api.patch(`/sublocations/${sub.id}`, { name: editName.trim(), job_code: editCode.trim() });
            setEditingId(null);
            toast.success("Sub-location updated");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const toggle = async (sub) => {
        try {
            await api.patch(`/sublocations/${sub.id}`, { active: !sub.active });
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (sub) => {
        if (!window.confirm(`Delete "${sub.name}"?`)) return;
        try {
            await api.delete(`/sublocations/${sub.id}`);
            toast.success("Sub-location deleted");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sublocation-manager-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Sub-locations · {site?.name}
                    </DialogTitle>
                    <DialogDescription>
                        Data Halls technicians can pick at Clock Out. The Internal Job Code is
                        administrative only — employees never see it.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col sm:flex-row gap-2 items-end border-b border-slate-200 pb-4">
                    <div className="flex-1 space-y-1.5 w-full">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</Label>
                        <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                            placeholder="e.g. IAD-102.11" className="h-10" data-testid="new-sublocation-name" />
                    </div>
                    <div className="flex-1 space-y-1.5 w-full">
                        <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Internal Job Code</Label>
                        <Input value={newCode} onChange={(e) => setNewCode(e.target.value)}
                            placeholder="optional" className="h-10" data-testid="new-sublocation-job-code" />
                    </div>
                    <Button onClick={add} className="h-10 bg-[#0066D9] hover:bg-[#0052ad] text-white w-full sm:w-auto"
                        data-testid="add-sublocation-button">
                        <Plus size={16} className="mr-1.5" /> Add
                    </Button>
                </div>

                {loading ? (
                    <div className="py-8 text-center text-slate-500 text-sm">Loading…</div>
                ) : subs.length === 0 ? (
                    <div className="py-8 text-center text-slate-500 text-sm" data-testid="sublocations-empty">
                        No sub-locations yet for this site.
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {subs.map((s) => (
                            <div key={s.id} className="py-2.5 flex items-center gap-2" data-testid={`sublocation-row-${s.id}`}>
                                {editingId === s.id ? (
                                    <>
                                        <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                                            className="h-9 flex-1" data-testid={`edit-sublocation-name-${s.id}`} />
                                        <Input value={editCode} onChange={(e) => setEditCode(e.target.value)}
                                            placeholder="Job code" className="h-9 w-32" data-testid={`edit-sublocation-code-${s.id}`} />
                                        <Button size="sm" variant="ghost" className="h-9 px-2 text-emerald-600"
                                            onClick={() => saveEdit(s)} data-testid={`save-sublocation-${s.id}`}>
                                            <Check size={16} />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-9 px-2 text-slate-400"
                                            onClick={() => setEditingId(null)}>
                                            <X size={16} />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            className="flex-1 text-left min-w-0"
                                            onClick={() => { setEditingId(s.id); setEditName(s.name); setEditCode(s.job_code || ""); }}
                                            data-testid={`edit-sublocation-${s.id}`}
                                        >
                                            <div className="font-semibold text-[#003366] text-sm truncate">{s.name}</div>
                                            <div className="text-xs text-slate-500 font-mono">
                                                {s.job_code ? `Job code: ${s.job_code}` : "No job code"}
                                            </div>
                                        </button>
                                        {s.active ? (
                                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] uppercase">Active</Badge>
                                        ) : (
                                            <Badge className="bg-slate-200 text-slate-700 border border-slate-300 text-[10px] uppercase">Inactive</Badge>
                                        )}
                                        <Button size="sm" variant="ghost" className="h-9 px-2"
                                            onClick={() => toggle(s)} title={s.active ? "Deactivate" : "Activate"}
                                            data-testid={`toggle-sublocation-${s.id}`}>
                                            {s.active
                                                ? <ToggleRight size={18} weight="fill" className="text-emerald-600" />
                                                : <ToggleLeft size={18} className="text-slate-400" />}
                                        </Button>
                                        <Button size="sm" variant="ghost"
                                            className="h-9 px-2 text-[#E6002C] hover:bg-red-50"
                                            onClick={() => remove(s)} data-testid={`delete-sublocation-${s.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
